import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity, SafeAreaView,
  RefreshControl, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { rideService } from '../../services/rideService';

const RideHistoryScreen = ({ navigation }) => {
  const [rides, setRides] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { fetchRides(1); }, []);

  const fetchRides = async (p) => {
    try {
      if (p === 1) setLoading(true);
      const response = await rideService.getRideHistory(p, 20);
      if (response.success) {
        if (p === 1) {
          setRides(response.data.rides);
        } else {
          setRides(prev => [...prev, ...response.data.rides]);
        }
        setPage(p);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching ride history:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      COMPLETED: '#4CAF50', CANCELLED: '#F44336', IN_PROGRESS: '#2196F3',
      ACCEPTED: '#FF9800', REQUESTED: '#9E9E9E', ARRIVED: '#9C27B0',
    };
    return colors[status] || '#666';
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderRide = ({ item }) => (
    <TouchableOpacity
      style={styles.rideCard}
      onPress={() => navigation.navigate('TrackDriver', { ride: item })}
    >
      <View style={styles.rideHeader}>
        <Text style={styles.rideDate}>{formatDate(item.createdAt)}</Text>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
          <Text style={styles.statusText}>{item.status}</Text>
        </View>
      </View>

      <View style={styles.locationContainer}>
        <View style={styles.locationRow}>
          <View style={styles.pickupDot} />
          <Text style={styles.locationText} numberOfLines={1}>{item.origin}</Text>
        </View>
        <View style={styles.locationRow}>
          <View style={styles.destinationDot} />
          <Text style={styles.locationText} numberOfLines={1}>{item.destination}</Text>
        </View>
      </View>

      <View style={styles.rideFooter}>
        <Text style={styles.fareText}>
          ${(item.fare || item.estimatedFare || 0).toFixed(2)}
          {item.distance > 0 ? ` · ${Number(item.distance).toFixed(1)} mi` : ''}
        </Text>
        {item.driver && <Text style={styles.driverText}>{item.driver.name}</Text>}
        {item.rating && (
          <View style={styles.ratingRow}>
            <Ionicons name="star" size={14} color="#FFD700" />
            <Text style={styles.ratingText}>{item.rating}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <ActivityIndicator size="large" color="#000" style={{ flex: 1 }} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {rides.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons name="time-outline" size={64} color="#ccc" />
          <Text style={styles.emptyTitle}>No Rides Yet</Text>
          <Text style={styles.emptyText}>Your ride history will appear here</Text>
        </View>
      ) : (
        <FlatList
          data={rides}
          renderItem={renderRide}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContainer}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchRides(1); }} />}
          onEndReached={() => { if (page < totalPages) fetchRides(page + 1); }}
          onEndReachedThreshold={0.5}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  listContainer: { padding: 16 },
  rideCard: {
    backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12,
    elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1, shadowRadius: 2,
  },
  rideHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  rideDate: { fontSize: 14, color: '#666' },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4 },
  statusText: { color: '#fff', fontSize: 12, fontWeight: 'bold' },
  locationContainer: { marginBottom: 12 },
  locationRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  pickupDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#4CAF50', marginRight: 12 },
  destinationDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#F44336', marginRight: 12 },
  locationText: { flex: 1, fontSize: 14, color: '#333' },
  rideFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  fareText: { fontSize: 18, fontWeight: 'bold', color: '#333' },
  driverText: { fontSize: 14, color: '#666' },
  ratingRow: { flexDirection: 'row', alignItems: 'center' },
  ratingText: { fontSize: 14, color: '#666', marginLeft: 4 },
  emptyState: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyTitle: { fontSize: 22, fontWeight: 'bold', color: '#333', marginTop: 16 },
  emptyText: { fontSize: 16, color: '#666', marginTop: 8 },
});

export default RideHistoryScreen;
